import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getMyApps, deleteApp } from '../api/apps';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';
import { sanitizeError } from '../utils/errorHandler';
import { 
  Plus, 
  Smartphone, 
  Calendar, 
  Users, 
  Clock,
  Play,
  Edit,
  Trash2,
  ExternalLink,
  Sparkles
} from 'lucide-react';

const MyApps = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { isExpanded } = useSidebar();

  useEffect(() => {
    // Worker üzerinden verileri çek
    const fetchApps = async () => {
      try {
        if (!user) {
          setLoading(false);
          return;
        }

        const data = await getMyApps();
        setApps(data || []);
        setLoading(false);
      } catch (error) {
        logger.error('Uygulamalar çekilirken hata:', error);
        toast.error('Uygulamalar yüklenirken hata oluştu');
        setLoading(false);
      }
    };

    fetchApps();
  }, [user]);

  const handleDeleteApp = async (appId) => {
    try {
      await deleteApp(appId);
      setApps(apps.filter(app => app.id !== appId));
      toast.success('Uygulama başarıyla silindi');
    } catch (error) {
      logger.error('Uygulama silinirken hata:', error);
      toast.error(sanitizeError(error));
    }
  };

  const handleStartTest = async (appId) => {
    try {
      // Dummy start test - gerçek API olmadığında
      setApps(apps.map(app => 
        app.id === appId 
          ? { ...app, status: 'testing', currentDay: 1 }
          : app
      ));
      toast.success('Test süreci başlatıldı');
    } catch (error) {
      logger.error('Test başlatılırken hata:', error);
      toast.error(sanitizeError(error));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200';
      case 'testing':
        return 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border border-blue-200';
      case 'completed':
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200';
      case 'cancelled':
        return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200';
      default:
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'testing':
        return 'Test Ediliyor';
      case 'completed':
        return 'Tamamlandı';
      case 'cancelled':
        return 'İptal Edildi';
      default:
        return status;
    }
  };

  const getPlatformIcon = (platform) => {
    return <Smartphone className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="relative">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-emerald-600"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Sparkles className="h-8 w-8 text-emerald-600 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Mobile Header Gradient */}
      <div className="md:hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Desktop Header Gradient */}
      <div className="hidden md:block bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-48 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className={`relative -mt-32 pb-8 transition-all duration-300 ${
        isExpanded ? 'md:ml-72' : 'md:ml-18'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="backdrop-blur-xl bg-white/20 rounded-2xl p-6 shadow-xl border border-white/30 overflow-hidden">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">Uygulamalarım</h1>
                    <p className="text-white/80">
                      Uygulamalarınızı yönetin ve test süreçlerini takip edin
                    </p>
                  </div>
                </div>
                <Link
                  to="/create-app"
                  className="inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-r from-white/20 to-white/30 backdrop-blur-sm text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-white/30 ml-2"
                  aria-label="Yeni Uygulama"
                >
                  <Plus className="h-6 w-6" />
                </Link>
              </div>
            </div>
          </div>

          {/* Apps Grid */}
          {apps.length === 0 ? (
            <div className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg p-12 border border-white/40">
              <div className="text-center">
                <Smartphone className="mx-auto h-16 w-16 text-emerald-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">Henüz uygulama yok</h3>
                <p className="mt-2 text-gray-600">
                  İlk uygulamanızı ekleyerek başlayın.
                </p>
                <div className="mt-6">
                  <Link
                    to="/create-app"
                    className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    <Plus className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform" />
                    Yeni Uygulama Ekle
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {apps.map((app) => (
                <div key={app.id} className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg border border-white/40 hover:shadow-xl transition-all duration-300 group overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                          <Smartphone className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-sm font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                          {app.platform}
                        </span>
                      </div>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(app.status)}`}>
                        {getStatusText(app.status)}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors">
                      {app.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                      {app.description}
                    </p>

                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4 p-3 backdrop-blur-sm bg-white/40 rounded-lg border border-white/30">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1 text-emerald-600" />
                        <span className="font-medium">{app.current_testers || 0}/{app.max_testers || 12} tester</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-teal-600" />
                        <span>{new Date(app.created_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>

                    {app.status === 'testing' && (
                      <div className="flex items-center text-sm text-blue-600 mb-4 p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <Clock className="h-4 w-4 mr-2" />
                        <span className="font-medium">Gün {app.currentDay}/14</span>
                      </div>
                    )}

                    <div className="flex space-x-2">
                      <Link
                        to={`/app/${app.id}`}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Detay
                      </Link>
                      
                      {app.status === 'active' && (app.current_testers || 0) >= 12 && (
                        <button
                          onClick={() => handleStartTest(app.id)}
                          className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Test Başlat
                        </button>
                      )}

                      <button
                        onClick={() => handleDeleteApp(app.id)}
                        className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-red-500 to-rose-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyApps;